import * as React from "react";
import { NotificationSubscription } from "webportalclient";
import { MatchSortCollectionHeader } from "../Components/MatchSortCollectionHeader";
import { MatchSortHeader } from "../Components/MatchSortHeader";
import { ODataParams, Query, useQuery } from "../Components/Query";
import { Column, Table } from "../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../Core/SecurityContext";
import { Switch } from "../forms/components/Switch";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { Permission } from "../misc/api/apiTypes";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";
import { uniqBy } from "../misc/ArrayFunctions";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { Authorization } from "../misc/components/Authorization";
import DocumentTitle from "../misc/components/DocumentTitle";
import { HamburgerMenu } from "../misc/components/HamburgerMenu";
import { CreateNotification } from "./CreateNotification";
import { EditNotification } from "./EditNotification";
import { NotificationSettings } from "./NotificationSettings";


export const NotificationsPanelTable = () => {
    const [showCreate, setShowCreate] = React.useState(false);
    const [editNotificationRecord, setEditNotificationRecord] = React.useState<NotificationSubscription | undefined>();
    const [userOptions, setUserOptions] = React.useState([]);
    const [roleOptions, setRoleOptions] = React.useState([]);
    const [pluginNames, setPluginNames] = React.useState([]);
    const [error, setError] = React.useState<string | undefined>();
    const { data, applyFilters, filters, refresh } = useQuery();
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();
    const client = useWebPortalApiClient();

    React.useEffect(() => {
        const getUsers = async () => {
            const result = await client.client.getNotificationSubscriptions("usersToNotify,rolesToNotify,pluginName", undefined, undefined, "usersToNotify,rolesToNotify");

            const users = result.value.flatMap(v => v.usersToNotify);
            const userOptionsUnique = uniqBy(users, (a, b) => a.email === b.email).map(u => ({
                value: u.email,
                label: u.email
            }));

            setUserOptions(userOptionsUnique);

            const roles = result.value.flatMap(v => v.rolesToNotify);
            const roleOptionsUnique = uniqBy(roles, (a, b) => a.name === b.name).map(u => ({
                value: u.name,
                label: u.name
            }));

            setRoleOptions(roleOptionsUnique);


            const pluginNamesResult = uniqBy(result.value, (a, b) => a.pluginName === b.pluginName).map(p => ({
                value: p.pluginName,
                label: p.pluginName
            }))
            setPluginNames(pluginNamesResult);
        }

        getUsers();
    }, []);

    const onDelete = async (id: string) => {
        try {
            setError(undefined);
            await client.client.deleteNotificationSubscription(id);
        } catch (ex) {
            setError(ex.message);
        }
        refresh();
    }

    const onActiveToggle = async (subscription: NotificationSubscription) => {
        await client.client.patchNotificationSubscription({
            active: !subscription.active
        }, subscription.id);
        refresh();
    }

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const checkboxFormatter = (value) => {
        if (value)
            return (<i className="text-success fas fa-check"></i>);
        else
            return (<i className="text-danger fas fa-times"></i>);
    }

    const RenderHamburgerMenu = (subscription: NotificationSubscription, currentPermissions: Permission[], currentStudyId: string) => {
        return (
            <HamburgerMenu>
                <Authorization permissionId={PermissionEnum.EditNotificationSubscription}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <li>
                        <a onClick={() => { setEditNotificationRecord(subscription) }}>Edit</a>
                    </li>
                </Authorization>
                <Authorization permissionId={PermissionEnum.EditNotificationSubscription}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <li>
                        <a onClick={() => onDelete(subscription.id)}>Delete</a>
                    </li>
                </Authorization>
            </HamburgerMenu>
        );
    };

    const booleanOptions = [{
        value: false,
        label: "No",
    }, {
        value: true,
        label: "Yes",
    }]

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Notification Management</h2>
                </div>
                <div className="col-md-6 h2">
                    <div className="pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateNotificationSubscription}>
                            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>New Notification</button>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary btn-outline" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default" data-automationid="NotificationsPanelTable">
                        <div className="panel-body">
                            <Alert category={AlertCategoryEnum.Danger} show={error !== undefined}>{error}</Alert>
                            <Table data={data}>
                                <Column field="pluginName" header={<MatchSortHeader title="Type" field="pluginName" options={pluginNames} sortField="pluginName" />} formatter={pn => pn} />
                                <Column field="" header={<MatchSortCollectionHeader title="Notify Roles" primarySubClauseField="name" options={roleOptions} orderField="rolesToNotify" sortEnabled={false} />} formatter={ns => ns.rolesToNotify.map(r => r.name).join(", ")} />
                                <Column field="" header={<MatchSortCollectionHeader title="Notify Users" primarySubClauseField="email" options={userOptions} orderField="usersToNotify" sortEnabled={false} />} formatter={ns => ns.usersToNotify.map(u => u.email).join(", ")} />
                                <Column field="notifyAuthor" header={<MatchSortHeader title="Notify Author" field="notifyAuthor" options={booleanOptions} />} formatter={checkboxFormatter} />
                                <Column field="notifySite" header={<MatchSortHeader title="Notify Site" field="notifySite" options={booleanOptions} />} formatter={checkboxFormatter} />
                                <Column field="" header={<MatchSortHeader title="Active" field="active" options={booleanOptions} sortField="active" />} formatter={ns => <Switch isToggledOn={ns.active} onChange={() => onActiveToggle(ns)} />} />
                                <Column field="" formatter={n => RenderHamburgerMenu(n, currentUserPermissions, study.id)} />
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
            {showCreate ? <CreateNotification onClose={() => { setShowCreate(false); refresh(); }} /> : null}
            {editNotificationRecord ? <EditNotification existingSubscription={editNotificationRecord} onClose={() => { setShowCreate(false); refresh(); setEditNotificationRecord(undefined) }} /> : null}
        </>
    );
}

export const NotificationsPanel = () => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    const handleRefresh = async (params: ODataParams) => {

        let filter = params.filter ? `(${params.filter}) and ` : '';
        filter += `studyId eq ${study.id}`;

        return await client.client.getNotificationSubscriptions(undefined, filter, params.orderby, "usersToNotify,rolesToNotify", params.top, params.skip, undefined, true);
    }

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewNotificationSubscriptions}>
            <DocumentTitle title="Notification Management | Vitalograph Web Portal">
                <Query onRefresh={handleRefresh}>
                    <NotificationsPanelTable />
                </Query>
                <NotificationSettings />
            </DocumentTitle>
        </Authorization>
    );
}
